import React, { useEffect, useState } from "react";
import { Card, Button, Tag, Descriptions, Row, Col, Spin, message, Table, Progress, Space, Popconfirm } from "antd";
import { ArrowLeftOutlined, SendOutlined, EyeOutlined, ReadOutlined, RiseOutlined, RedoOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import pushBroadcastApi from "../../../api/push-broadcast-api";
import { asObject } from "./push-form-helpers";

// Thẻ số liệu nhỏ: icon nền màu nhạt + số to + nhãn.
function MiniStat({ icon, label, value, suffix, color }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}1a`, color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                {icon}
            </div>
            <div>
                <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{value}{suffix ? <span style={{ fontSize: 13, color: "#8c8c8c" }}> {suffix}</span> : null}</div>
                <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 2 }}>{label}</div>
            </div>
        </div>
    );
}

const AUDIENCE_TAG = {
    all: { color: "blue", label: "Tất cả" },
    roles: { color: "green", label: "Theo vai trò" },
    criteria: { color: "purple", label: "Bộ lọc" },
    single: { color: "default", label: "Cá nhân" },
    ids: { color: "default", label: "Danh sách ID" },
};

const STATUS_TAG = {
    sent: { color: "green", label: "Đã gửi" },
    scheduled: { color: "blue", label: "Đã lên lịch" },
    sending: { color: "processing", label: "Đang gửi" },
    cancelled: { color: "default", label: "Đã hủy" },
    failed: { color: "red", label: "Gửi lỗi" },
};

const OPTION_LABELS = {
    imageUrl: "Ảnh lớn",
    icon: "Icon",
    color: "Màu nhấn",
    badge: "Badge",
    priority: "Độ ưu tiên",
    silent: "Im lặng",
    ttl: "TTL (giây)",
    collapseKey: "Collapse key",
};

function PushBroadcastDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [broadcast, setBroadcast] = useState(null);
    const [stats, setStats] = useState({ notificationCount: 0, distinctUsersOpened: 0, distinctUsersRead: 0 });
    const [loading, setLoading] = useState(true);

    const fetchDetail = async () => {
        try {
            const { data } = await pushBroadcastApi.getBroadcast(id);
            setBroadcast(data.data.broadcast);
            setStats(data.data.stats || {});
        } catch {
            message.error("Không thể tải chi tiết push notification");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleCancel = async () => {
        try {
            await pushBroadcastApi.cancelBroadcast(id);
            message.success("Đã hủy lịch gửi");
            fetchDetail();
        } catch (err) {
            message.error(err?.response?.data?.message || "Hủy thất bại");
        }
    };

    const handleDelete = async () => {
        try {
            await pushBroadcastApi.deleteBroadcast(id);
            message.success("Đã xóa");
            navigate("/push");
        } catch (err) {
            message.error(err?.response?.data?.message || "Xóa thất bại");
        }
    };

    if (loading) {
        return <div className="flex-1 flex items-center justify-center"><Spin size="large" /></div>;
    }
    if (!broadcast) return null;

    const audienceTag = AUDIENCE_TAG[broadcast.audienceType] || { color: "default", label: broadcast.audienceType };
    const statusTag = STATUS_TAG[broadcast.status] || STATUS_TAG.sent;
    const openRate = stats.notificationCount > 0 ? Math.round((stats.distinctUsersOpened / stats.notificationCount) * 100) : 0;
    // Gửi bằng Topic: push không mang notification_id riêng từng người nên KHÔNG có
    // dữ liệu mở/đọc. Vẽ 0% là bịa ra con số sai — phải nói rõ là không đo được.
    const viaTopic = broadcast?.sendMode === "topic";
    const readRate = stats.notificationCount > 0 ? Math.round((stats.distinctUsersRead / stats.notificationCount) * 100) : 0;

    const payload = asObject(broadcast.audiencePayload);
    const audienceUserIds = payload.userIds ?? (payload.userId != null ? [payload.userId] : null);
    const audienceRoles = payload.roles;
    const audienceCriteria = payload.mode === "criteria" ? payload : null;
    const optsObj = asObject(broadcast.pushOptions);
    const opts = Object.keys(optsObj).length > 0 ? optsObj : null;

    return (
        <div className="flex-1 p-6 overflow-auto max-w-3xl" style={{ background: "rgb(var(--surface-sunken))" }}>
            <div className="flex items-center gap-3 mb-4">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/push")}>Quay lại</Button>
                <h1 className="text-xl font-semibold m-0" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{broadcast.title}</h1>
                <Tag color={statusTag.color}>{statusTag.label}</Tag>
                <Space>
                    <Button icon={<RedoOutlined />} onClick={() => navigate(`/push/new?from=${broadcast.broadcastId}`)}>Gửi lại</Button>
                    {broadcast.status === "scheduled" ? (
                        <Popconfirm title="Hủy lịch gửi này?" onConfirm={handleCancel} okText="Hủy lịch" cancelText="Thôi">
                            <Button danger>Hủy lịch</Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm title="Xóa chiến dịch này?" onConfirm={handleDelete} okText="Xóa" cancelText="Thôi">
                            <Button danger icon={<DeleteOutlined />}>Xóa</Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            <Card className="mb-4" style={{ borderRadius: 12 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}><MiniStat icon={<SendOutlined />} label="Đã gửi" value={stats.notificationCount?.toLocaleString() ?? 0} color="#1677ff" /></Col>
                    {!viaTopic && (
                        <>
                            <Col xs={12} md={6}><MiniStat icon={<EyeOutlined />} label="Đã mở" value={stats.distinctUsersOpened?.toLocaleString() ?? 0} color="#52c41a" /></Col>
                            <Col xs={12} md={6}><MiniStat icon={<ReadOutlined />} label="Đã đọc" value={(stats.distinctUsersRead ?? 0).toLocaleString()} color="#13c2c2" /></Col>
                            <Col xs={12} md={6}><MiniStat icon={<RiseOutlined />} label="Tỷ lệ mở" value={openRate} suffix="%" color="#722ed1" /></Col>
                        </>
                    )}
                </Row>
                {viaTopic && (
                    <div className="mt-4 text-xs text-content-3">
                        Gửi bằng <strong>Topic</strong>: một request cho cả app nên Google không báo lại
                        từng máy. Vì vậy không có số liệu mở/đọc — đây là đánh đổi đã biết, không phải lỗi.
                        Số "Đã gửi" là số bản ghi trong hộp thư, vẫn chính xác.
                    </div>
                )}
                {!viaTopic && stats.notificationCount > 0 && (
                    <div className="mt-4">
                        <div className="text-xs text-content-3 mb-1">Tỷ lệ mở</div>
                        <Progress percent={openRate} strokeColor="#52c41a" />
                        <div className="text-xs text-content-3 mb-1 mt-2">Tỷ lệ đọc</div>
                        <Progress percent={readRate} strokeColor="#13c2c2" />
                    </div>
                )}
            </Card>

            <Card title="Thông tin thông báo" className="mb-4">
                <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Tiêu đề">{broadcast.title}</Descriptions.Item>
                    <Descriptions.Item label="Nội dung">{broadcast.message}</Descriptions.Item>
                    <Descriptions.Item label="Trạng thái"><Tag color={statusTag.color}>{statusTag.label}</Tag></Descriptions.Item>
                    {broadcast.scheduledAt && (
                        <Descriptions.Item label="Hẹn gửi lúc">{dayjs(Number(broadcast.scheduledAt)).format("DD/MM/YYYY HH:mm")}</Descriptions.Item>
                    )}
                    {broadcast.lastError && (
                        <Descriptions.Item label="Lỗi gần nhất">
                            <span style={{ color: "#cf1322" }}>{broadcast.lastError}</span>
                            {broadcast.failCount > 0 && <span className="text-content-3"> (thử {broadcast.failCount} lần)</span>}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Đối tượng"><Tag color={audienceTag.color}>{audienceTag.label}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Số user mục tiêu">{broadcast.targetUserCount?.toLocaleString() ?? "-"}</Descriptions.Item>
                    <Descriptions.Item label="Cách gửi">
                        {viaTopic
                            ? <Tag color="volcano">Topic — 1 request cho cả app</Tag>
                            : <Tag>Lần lượt — 1 lượt gửi mỗi người</Tag>}
                    </Descriptions.Item>
                    {broadcast.openPath && <Descriptions.Item label="Đường dẫn">{broadcast.openPath}</Descriptions.Item>}
                    {broadcast.openUrl && <Descriptions.Item label="URL">{broadcast.openUrl}</Descriptions.Item>}
                    <Descriptions.Item label="Tạo bởi">{broadcast.createdBy || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Thời gian">{broadcast.createdAt ? dayjs(Number(broadcast.createdAt)).format("DD/MM/YYYY HH:mm:ss") : "-"}</Descriptions.Item>
                </Descriptions>
            </Card>

            {opts && (
                <Card title="Tùy chọn nâng cao" className="mb-4">
                    <Descriptions column={1} bordered size="small">
                        {Object.entries(opts).map(([k, v]) => (
                            <Descriptions.Item key={k} label={OPTION_LABELS[k] || k}>
                                {k === "color" ? <Tag color={String(v)}>{String(v)}</Tag> : k === "silent" ? (v ? "Có" : "Không") : String(v)}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                </Card>
            )}

            {audienceUserIds && audienceUserIds.length > 0 && (
                <Card title={`Danh sách User ID (${audienceUserIds.length})`} className="mb-4">
                    <Table size="small" pagination={audienceUserIds.length > 20 ? { pageSize: 20 } : false}
                        dataSource={audienceUserIds.map((uid) => ({ uid }))} rowKey="uid"
                        columns={[{ title: "User ID", dataIndex: "uid", render: (uid) => <code>{uid}</code> }]} />
                </Card>
            )}

            {audienceRoles && audienceRoles.length > 0 && (
                <Card title="Vai trò mục tiêu" className="mb-4">
                    <div className="flex gap-2 flex-wrap">{audienceRoles.map((r) => <Tag key={r} color="green">{r}</Tag>)}</div>
                </Card>
            )}

            {audienceCriteria && (
                <Card title="Bộ lọc đối tượng" className="mb-4">
                    <Descriptions column={1} bordered size="small">
                        {audienceCriteria.platform && <Descriptions.Item label="Nền tảng"><Tag>{audienceCriteria.platform}</Tag></Descriptions.Item>}
                        {audienceCriteria.active != null && <Descriptions.Item label="Trạng thái tài khoản">{audienceCriteria.active ? "Đang hoạt động" : "Bị khóa"}</Descriptions.Item>}
                        {audienceCriteria.joinedAfter && <Descriptions.Item label="Tham gia sau">{dayjs(audienceCriteria.joinedAfter * 1000).format("DD/MM/YYYY")}</Descriptions.Item>}
                        {audienceCriteria.joinedBefore && <Descriptions.Item label="Tham gia trước">{dayjs(audienceCriteria.joinedBefore * 1000).format("DD/MM/YYYY")}</Descriptions.Item>}
                        {audienceCriteria.roles?.length > 0 && <Descriptions.Item label="Vai trò">{audienceCriteria.roles.map((r) => <Tag key={r} color="green">{r}</Tag>)}</Descriptions.Item>}
                    </Descriptions>
                </Card>
            )}
        </div>
    );
}

export default PushBroadcastDetail;
